import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { Modal } from 'src/components/modals/comps';
import Input, { Textarea } from 'src/components/form/Input';
import { MultiSelect } from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { CsvValidationErrors, SCVSummary } from 'src/components/CsvUpload';
import { parseGraphqlCsvValidationErrors, ValidationErrorItem } from 'src/utils/csv';
import { showSuccessToast } from 'src/components/Toast';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { useAuth } from 'src/state/auth';
import { Stepper } from 'src/components/Wizzard/comps';
import {
  CREATE_SCENARIO_GROUP_FROM_IMPORT,
  CreateScenarioGroupFromImportMutation,
  CreateScenarioGroupFromImportMutationVariables,
  UPSERT_CONTENT_PACK_ACCESS,
  UpsertContentPackAccessMutation,
  UpsertContentPackAccessMutationVariables,
} from 'src/graphql/contentImport';
import { ScenarioPackImportPayload } from 'src/types/contentImport';
import { generateScenarioPackTemplateLink, parseScenarioPackCsv } from './import';

const StyledModal = styled(Modal)`
  width: 820px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const StepperWrap = styled.div`
  padding: 0 2px;
`;

const StepPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const TemplateHint = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 14px 18px;

  a {
    color: ${({ theme }) => theme.colors.error};
    text-decoration: none;
    font-weight: 500;
  }
`;

const FormInput = styled(Input)`
  &[type='text'] {
    padding: 0 18px;
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 118px;
  padding: 14px 18px;
`;

const UploadBox = styled.div<{ $dragActive?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme, $dragActive }) => ($dragActive ? theme.colors.stroke : theme.colors.cultured)};
  min-height: 128px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  cursor: pointer;
  transition: background 120ms ease-in-out;
`;

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const PublishGrid = styled.div`
  display: grid;
  gap: 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-shrink: 0;
  }

  .end {
    margin-left: auto;
  }
`;

const PrimaryActionButton = styled(Button)`
  width: auto;
  min-width: max-content;
  padding-left: 2rem;
  padding-right: 2rem;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
  }
`;

interface OrgUnitOption {
  value: string;
  label: string;
}

type AddScenarioPackStepId = 'create' | 'edit' | 'publish';

interface AddScenarioPackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}

export const AddScenarioPackModal: React.FC<AddScenarioPackModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { flatOrganisations, workspaceId } = useAuth();
  const { variables: workspaceVariables, isLoading: isVariablesLoading } = useWorkspaceVariables();

  const [step, setStep] = useState(0);

  const [file, setFile] = useState<File | null>(null);
  const [packName, setPackName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [scenarioPayload, setScenarioPayload] = useState<ScenarioPackImportPayload | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState<string[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const [runCreateScenarioGroup, { loading: isCreating }] = useMutation<
    CreateScenarioGroupFromImportMutation,
    CreateScenarioGroupFromImportMutationVariables
  >(CREATE_SCENARIO_GROUP_FROM_IMPORT);

  const [runUpsertContentPackAccess, { loading: isPublishingTargets }] = useMutation<
    UpsertContentPackAccessMutation,
    UpsertContentPackAccessMutationVariables
  >(UPSERT_CONTENT_PACK_ACCESS);

  const isSubmitting = isCreating || isPublishingTargets;

  const templateLink = useMemo(() => generateScenarioPackTemplateLink(), []);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(templateLink);
    };
  }, [templateLink]);

  const organisationOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'O' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const workspaceOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'W' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const resetState = () => {
    setStep(0);
    setFile(null);
    setPackName('');
    setDescription('');
    setAuthor('');
    setScenarioPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);
    setSelectedOrganisationIds([]);
    setSelectedWorkspaceIds(workspaceId ? [workspaceId] : []);
    setError('');
    setIsParsing(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetState();
  }, [isOpen, workspaceId]);

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    if (isVariablesLoading) {
      setError('Workspace variables are still loading. Please try again in a moment.');
      return;
    }

    setFile(selectedFile);
    setError('');
    setIsParsing(true);
    setScenarioPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);

    try {
      const parsed = await parseScenarioPackCsv({
        file: selectedFile,
        variables: workspaceVariables,
        existingScenarioIds: [],
        requirePackName: false,
      });

      setScenarioPayload(parsed.payload);
      setValidationErrors(parsed.validationErrors);
      setCreatedCount(parsed.createdCount);
      setUpdatedCount(parsed.updatedCount);

      if (parsed.payload?.group?.name && !packName.trim()) {
        setPackName(parsed.payload.group.name);
      }

      if (parsed.payload?.group?.description && !description.trim()) {
        setDescription(parsed.payload.group.description);
      }

      if (parsed.payload?.group?.author && !author.trim()) {
        setAuthor(parsed.payload.group.author);
      }
    } finally {
      setIsParsing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
    disabled: isVariablesLoading || isSubmitting,
  });

  const hasValidatedFile = !!scenarioPayload && !validationErrors.length && !isVariablesLoading && !isParsing;
  const canProceedToEdit = hasValidatedFile && !!packName.trim() && !isSubmitting;
  const canProceedToPublish = canProceedToEdit;
  const hasPublishTargets = selectedOrganisationIds.length > 0 || selectedWorkspaceIds.length > 0;
  const canPublish = canProceedToPublish && hasPublishTargets && !isSubmitting;

  const slides = useMemo<{ title: string; id: AddScenarioPackStepId; locked?: boolean }[]>(
    () => [
      {
        title: 'Create scenario pack',
        id: 'create',
      },
      {
        title: 'Review details',
        id: 'edit',
        locked: !canProceedToEdit,
      },
      {
        title: 'Publish',
        id: 'publish',
        locked: !canProceedToPublish,
      },
    ],
    [canProceedToEdit, canProceedToPublish],
  );

  const currentStepId = slides[step]?.id;

  const onChangeSlide = (id: AddScenarioPackStepId) => {
    const idx = slides.findIndex((slide) => slide.id === id);
    if (idx < 0 || slides[idx].locked) return;
    setStep(idx);
    setError('');
  };

  const publishScenarioPack = async () => {
    if (!scenarioPayload || !packName.trim() || !hasPublishTargets) return;

    setError('');

    const input: ScenarioPackImportPayload = {
      ...scenarioPayload,
      group: {
        ...scenarioPayload.group,
        id: null,
        name: packName.trim(),
        description: description.trim() || null,
        author: author.trim() || null,
        authorLogoPath: null,
        isSystem: true,
      },
    };

    try {
      const { data } = await runCreateScenarioGroup({
        variables: {
          input,
        },
      });

      const summary = data?.createScenarioGroupFromImport;
      if (!summary?.groupId) {
        throw new Error('Scenario pack publish failed.');
      }

      await runUpsertContentPackAccess({
        variables: {
          packType: 'scenario',
          packId: summary.groupId,
          organisations: selectedOrganisationIds,
          workspaces: selectedWorkspaceIds,
        },
      });

      await onCreated();
      showSuccessToast({ title: 'Scenario pack published' });
      handleClose();
    } catch (err) {
      const parsedValidationErrors = parseGraphqlCsvValidationErrors(err);
      if (parsedValidationErrors.length > 0) {
        setValidationErrors(parsedValidationErrors);
        setStep(0);
      }

      setError(err instanceof Error ? err.message : 'Scenario pack publish failed.');
    }
  };

  const onNext = async () => {
    if (currentStepId === 'create') {
      if (!canProceedToEdit) return;
      setStep(1);
      return;
    }

    if (currentStepId === 'edit') {
      if (!canProceedToPublish) return;
      setStep(2);
      return;
    }

    await publishScenarioPack();
  };

  const nextDisabled =
    (currentStepId === 'create' && !canProceedToEdit) ||
    (currentStepId === 'edit' && !canProceedToPublish) ||
    (currentStepId === 'publish' && !canPublish);

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSubmitting}>
      <Content>
        <Poppins className="m-title" px={32}>
          Add scenario pack
        </Poppins>

        <Spacer $px={18} />
        <StepperWrap>
          <Stepper slides={slides} step={step} onChangeSlide={onChangeSlide} maxSteps={3} />
        </StepperWrap>

        <Spacer $px={18} />
        <StepPanel>
          {currentStepId === 'create' && (
            <>
              <TemplateHint>
                <Poppins px={14}>
                  <a href={templateLink} download="scenario_pack_template.csv">
                    Click here
                  </a>{' '}
                  to download the CSV template for scenario pack
                </Poppins>
              </TemplateHint>

              <Spacer $px={20} />
              <InputLabel>NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={packName}
                onChange={(e) => setPackName(e.target.value)}
                placeholder="Enter scenario pack name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>DESCRIPTION</InputLabel>
              <Spacer $px={8} />
              <FormTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this scenario pack"
                maxLength={2048}
              />

              <Spacer $px={16} />
              <InputLabel>AUTHOR</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Enter author"
                maxLength={255}
                disabled={isSubmitting}
              />

              <Spacer $px={16} />
              <InputLabel>UPLOAD A CSV FILE</InputLabel>
              <Spacer $px={8} />
              <UploadBox {...getRootProps()} $dragActive={isDragActive}>
                <input {...getInputProps()} />
                <Poppins px={16} weight={500}>
                  {file
                    ? `File selected: ${file.name}`
                    : isDragActive
                    ? 'Drop the CSV file here'
                    : 'Click to upload or drag and drop CSV file'}
                </Poppins>
              </UploadBox>

              {isParsing && (
                <>
                  <Spacer $px={10} />
                  <Poppins px={14}>Validating CSV file...</Poppins>
                </>
              )}

              <Spacer $px={16} />
              <CsvValidationErrors errors={validationErrors} />

              {hasValidatedFile && (
                <>
                  <SummaryCard>
                    <SCVSummary
                      items={[
                        'File validated',
                        `${createdCount} scenarios ready to be created`,
                        updatedCount ? `${updatedCount} scenarios ready to be updated` : '',
                      ].filter(Boolean)}
                    />
                  </SummaryCard>
                </>
              )}
            </>
          )}

          {currentStepId === 'edit' && (
            <>
              <InputLabel>NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={packName}
                onChange={(e) => setPackName(e.target.value)}
                placeholder="Enter scenario pack name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>DESCRIPTION</InputLabel>
              <Spacer $px={8} />
              <FormTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this scenario pack"
                maxLength={2048}
              />

              <Spacer $px={16} />
              <InputLabel>AUTHOR</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Enter author"
                maxLength={255}
                disabled={isSubmitting}
              />

              <Spacer $px={16} />
              <SummaryCard>
                <SummaryRow>
                  <div>
                    <InputLabel>CSV FILE</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{file?.name || 'No file selected'}</Poppins>
                  </div>
                  <div>
                    <InputLabel>SCENARIOS</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{createdCount + updatedCount}</Poppins>
                  </div>
                </SummaryRow>
              </SummaryCard>
            </>
          )}

          {currentStepId === 'publish' && (
            <PublishGrid>
              <SummaryCard>
                <SummaryRow>
                  <div>
                    <InputLabel>SCENARIO PACK</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14} weight={500}>
                      {packName.trim()}
                    </Poppins>
                  </div>
                  <div>
                    <InputLabel>SCENARIOS</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{createdCount + updatedCount}</Poppins>
                  </div>
                </SummaryRow>
              </SummaryCard>

              <div>
                <InputLabel>PUBLISH TO ORGANISATIONS</InputLabel>
                <Spacer $px={8} />
                <MultiSelect
                  options={organisationOptions}
                  value={selectedOrganisationIds}
                  onChange={setSelectedOrganisationIds}
                  placeholder="Select organisations"
                  selectedLabel="selected"
                />
              </div>

              <div>
                <InputLabel>PUBLISH TO WORKSPACES</InputLabel>
                <Spacer $px={8} />
                <MultiSelect
                  options={workspaceOptions}
                  value={selectedWorkspaceIds}
                  onChange={setSelectedWorkspaceIds}
                  placeholder="Select workspaces"
                  selectedLabel="selected"
                />
              </div>

              {!hasPublishTargets && (
                <Poppins px={14} color="error">
                  Select at least one organisation or workspace to publish this scenario pack.
                </Poppins>
              )}
            </PublishGrid>
          )}
        </StepPanel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          {step > 0 ? (
            <Button secondary onClick={() => setStep((prev) => Math.max(0, prev - 1))} disabled={isSubmitting}>
              Previous
            </Button>
          ) : (
            <div />
          )}

          <PrimaryActionButton
            primary
            className="end"
            onClick={onNext}
            disabled={nextDisabled}
            loading={isSubmitting}
          >
            {currentStepId === 'publish' ? 'Publish scenario pack' : 'Next'}
          </PrimaryActionButton>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
